import { useEffect, useMemo, useState } from 'react';
import { Common } from '@stockbit/ui';
import { useRouter } from 'next/router';

import {
  virtualTradingAction,
  VIRTUAL_TRADING_TYPES,
} from 'lib/analytics/virtualTrading';
import { ORDER_TYPE } from 'constants/trading';
import { formatNumber } from 'utils/number';
import { getColorBasedOnNumber } from 'utils/general';
import tradingOperationStore from 'lib/stores/tradingOperation';
import tradingDataStore from 'lib/stores/tradingData';
import { useVirtualOrderStore } from 'global/VirtualTrading/stores/useVirtualOrderStore';
import {
  getBuyLot,
  getSellLot,
  getSellGain,
  getSellProfitLoss,
} from 'global/VirtualTrading/utils/formula';
import Slider from 'global/Securities/components/Slider';
import usePortfolioStore from 'global/VirtualTrading/stores/usePortfolioStore';
import FormItem from '../components/FormItem';
import generateStepPrice from '../utils/generateStepPrice';
import { ButtonWrapper, OrderButton } from '../styled';
import { lotSize } from '../constant';
import virtualTradingStore from './store';
import NumberInput from '../components/NumberInput';

const { Text } = Common;

interface iFormSell {
  orderType: string;
  orderid?: string;
  orderedLot: number;
  orderedPrice: number;
  closeModal: () => void;
}

function FormSell({
  orderType,
  orderid = undefined,
  orderedLot,
  orderedPrice,
  closeModal,
}: iFormSell) {
  const router = useRouter();

  const [isButtonLoading, setIsButtonLoading] = useState<boolean>(false);
  const {
    portfolio: {
      price: { average: priceAverage },
      available_lot,
    },
  } = usePortfolioStore();
  const { price, shares, setPrice, setShares } = virtualTradingStore();
  const { company, getOrderHistory, getPortfolio } = tradingDataStore();
  const { postTradeSellData } = tradingOperationStore();

  const createVirtualOrder = useVirtualOrderStore((s) => s.createVirtualOrder);
  const createVirtualAmendOrder = useVirtualOrderStore(
    (s) => s.createVirtualAmendOrder,
  );
  const { avalilableLot, maxLot } = useMemo(() => {
    if (
      orderType === ORDER_TYPE.VIRTUAL_AMEND_SELL &&
      Number(orderedPrice) === Number(price)
    ) {
      return { avalilableLot: available_lot + orderedLot, maxLot: orderedLot };
    }
    if (
      orderType === ORDER_TYPE.VIRTUAL_AMEND_SELL &&
      Number(orderedPrice) !== Number(price)
    ) {
      return {
        avalilableLot: available_lot + orderedLot,
        maxLot: available_lot + orderedLot,
      };
    }
    return { avalilableLot: available_lot, maxLot: available_lot };
  }, [available_lot, orderedLot, orderType, price, orderedPrice]);

  if (!company) return null;

  const { isLoading } = postTradeSellData;
  const symbol = company?.data?.symbol ? company.data.symbol : '';
  const lot = shares / 100 > maxLot ? maxLot : shares / 100;
  const buyAmount = getBuyLot(lot, priceAverage);
  const sellLot = getSellLot(lot, price);
  const sellProfitLoss = getSellProfitLoss(lot, price, buyAmount);
  const sellGain = getSellGain(sellProfitLoss, buyAmount);

  const onSubmitOrder = async () => {
    try {
      if (isLoading) {
        return;
      }

      setIsButtonLoading(true);

      if (orderType === ORDER_TYPE.VIRTUAL_AMEND_SELL) {
        await createVirtualAmendOrder({
          gtc: false,
          order_id: orderid,
          price: Number(price),
          shares: Number(shares),
          symbol,
        });

        getOrderHistory(false);
        getPortfolio(false);

        virtualTradingAction.onAmendOrderPlaced({
          Symbol: symbol,
          Price: price,
          Type: VIRTUAL_TRADING_TYPES.virtualTrading,
        });

        closeModal();
        setIsButtonLoading(false);
      }

      if (orderType === ORDER_TYPE.VIRTUAL_SELL) {
        await createVirtualOrder('sell', symbol, {
          gtc: false,
          price: Number(price),
          shares: Number(shares),
          tradeshare: false,
        });

        virtualTradingAction.onSellOrderPlaced({
          Symbol: symbol,
          Price: price,
          gainLossPercentage: formatNumber(sellGain * lotSize, false, '0.00'),
          Type: VIRTUAL_TRADING_TYPES.virtualTrading,
        });

        closeModal();
        setIsButtonLoading(false);
        router.push('/trade/order');
      }
    } catch (error) {
      Promise.reject(error);
    }
  };

  const onLotChange = (lotValue) => {
    setShares(lotValue * 100);
  };

  const onPriceChange = (newPrice, lotValue) => {
    setPrice(newPrice);
    onLotChange(lotValue); // On Amend, changing price can also changing lot value
  };

  const initValue = () => {
    if (orderType === ORDER_TYPE.VIRTUAL_SELL) {
      onPriceChange(company.data?.price, avalilableLot);
    }

    if (orderType === ORDER_TYPE.VIRTUAL_AMEND_SELL) {
      onPriceChange(orderedPrice, orderedLot);
    }
  };

  useEffect(() => {
    initValue();
  }, []);

  return (
    <>
      <FormItem label="Available Lot">
        <Text align="right">{avalilableLot}</Text>
      </FormItem>
      <FormItem label="Sell Order Lot">
        <Slider min={0} max={maxLot} value={lot} onChange={onLotChange} />
      </FormItem>
      <FormItem label="">
        <NumberInput
          value={lot}
          min={0}
          max={maxLot}
          step={1}
          onChange={onLotChange}
        />
      </FormItem>
      <FormItem label="Price">
        <NumberInput
          value={price}
          min={1}
          step={generateStepPrice(price)}
          onChange={(v) => onPriceChange(v, lot)}
        />
      </FormItem>
      <FormItem label="Proceed Amount">
        <Text align="right">{formatNumber(sellLot, false)}</Text>
      </FormItem>
      <FormItem label="Profit/Loss">
        <Text align="right" color={getColorBasedOnNumber(sellProfitLoss)}>
          {sellProfitLoss > 0 && '+'}
          {formatNumber(sellProfitLoss, false)} ({sellGain > 0 && '+'}
          {formatNumber(sellGain * 100, false, '0.00')}%)
        </Text>
      </FormItem>
      <ButtonWrapper>
        <OrderButton
          disabled={shares < 100}
          onClick={() => {
            onSubmitOrder();
          }}
          loading={isButtonLoading}
          type="secondary"
          height="40px"
          radius="4px"
          block
        >
          <Text>Place Order</Text>
        </OrderButton>
      </ButtonWrapper>
    </>
  );
}

export default FormSell;
